import { Static, TSchema, Type } from '@sinclair/typebox';

export const CropDataSchema = Type.Object({
  x: Type.Number(),
  y: Type.Number(),
  width: Type.Number(),
  height: Type.Number(),
});
export type CropData = Static<typeof CropDataSchema>;

/**
 * Préfixe de recadrage Thumbor (`x1xy1:x2xy2/`) pour un crop stocké en pixels de
 * l'image d'origine, ou chaîne vide sans crop. Le préfixe précède TOUJOURS la
 * taille : `crop/800x0/<clé S3>`.
 *
 * Source unique de vérité : `transformToSchema`, `extractLogoUrl`, les favicons et
 * l'endpoint `/public-url` doivent produire exactement le même chemin — sinon la
 * même image signée à deux endroits donne deux URLs (cache Thumbor dédoublé) ou,
 * pire, un recadrage différent entre l'aperçu du builder et le site public.
 */
export function buildThumborCropPrefix(crop: CropData | undefined | null): string {
  if (!crop) return '';
  return `${crop.x}x${crop.y}:${crop.x + crop.width}x${crop.y + crop.height}/`;
}

export const CroppableImageSchema = Type.Object({
  url: Type.String(),
  crop: Type.Optional(CropDataSchema),
});
export type CroppableImage = Static<typeof CroppableImageSchema>;

export const CroppableImageResponseSchema = Type.Object(
  {
    url: Type.String(),
    crop: Type.Optional(CropDataSchema),
    preview_url: Type.String({ format: 'url' }),
    original_url: Type.String({ format: 'url' }),
  },
  { meta: { croppable_image: true } },
);
export type CroppableImageResponse = Static<typeof CroppableImageResponseSchema>;

export const THUMBOR_META_KEY = 'thumbor_sign_data';

/**
 * Qualité de compression Thumbor pour les images de CONTENU volumineuses (photos :
 * hero, couvertures d'article, galeries, cartes/photos de prestation). Thumbor sort
 * en q80 par défaut ; q76 réduit le poids de ~20-25 % sans perte perceptible sur des
 * photos, ce qui allège le LCP mobile. À NE PAS appliquer aux logos / visuels à texte
 * ou aplats nets (dégradation visible des bords) — cf. `ResizedImageLogoSchema`.
 *
 * `withThumborQuality('800x0')` → `'800x0/filters:quality(76)'`. Le filtre est placé
 * APRÈS le redimensionnement ; `transformToSchema` concatène `${crop}${size}/${key}`
 * → `crop/800x0/filters:quality(76)/key` (ordre Thumbor valide).
 * ⚠️ Change l'URL signée → bust du cache Thumbor (`RESULT_STORAGE_EXPIRATION_SECONDS=0`)
 * → ré-encodage plus léger au 1ᵉʳ hit après déploiement.
 */
export const IMG_CONTENT_QUALITY = 76;
export const withThumborQuality = (size: string): string => `${size}/filters:quality(${IMG_CONTENT_QUALITY})`;

/**
 * Image formats supported by the upload pipeline (Thumbor decoding).
 * AVIF/HEIC/etc. are blocked because the Thumbor image (`minimalcompact/thumbor`)
 * does not ship the Pillow plugins required to decode them.
 */
export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;

export type AllowedImageMimeType = (typeof ALLOWED_IMAGE_MIME_TYPES)[number];

export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'] as const;

/**
 * Taille max d'un fichier image accepté à l'upload. Garde-fou UX côté client
 * (pas une barrière de sécurité : l'upload va direct navigateur → S3 via URL
 * presignée) — la compression client ramène la quasi-totalité des photos très
 * en dessous de cette limite avant l'envoi.
 */
export const IMAGE_MAX_UPLOAD_SIZE_BYTES = 15 * 1024 * 1024;

/**
 * Image extensions explicitly known to be unsupported. Used to reject uploads
 * whose mime type is generic (e.g. `application/octet-stream`) but whose
 * extension reveals an unsupported format.
 */
export const UNSUPPORTED_IMAGE_EXTENSIONS = ['.avif', '.heic', '.heif', '.bmp', '.tif', '.tiff', '.svg'] as const;

/**
 * Factory for a single-image "picture" schema: the backend `transformToSchema`
 * layer detects `meta.picture: true` on a Type.Object and generates a pre-signed
 * Thumbor URL for each declared size. The stored value is the raw S3 key; the
 * returned object is `{ url, original_url, [size]: signed_url, ... }`.
 *
 *   PictureSchema({ sm: '400x0', md: '800x0', lg: '1200x0' }, { title: '...' })
 *
 * Consumers compose an <img srcset> from the signed per-size URLs.
 */
export function PictureSchema<TSizes extends Record<string, string>>(
  sizes: TSizes,
  options: { title?: string; description?: string; additionalMeta?: Record<string, unknown> } = {},
): TSchema {
  // Shape: `{ url?, original_url?, sm?, md?, lg?, xl? }`. Write side needs
  // only `url` (the raw S3 key stored in DB); `transformToSchema` generates
  // all signed per-size URLs at read time using the `THUMBOR_META_KEY` specs
  // declared on each size property.
  //
  // All fields are Optional so the builder can write `{ url }` alone and
  // so legacy data migrated to an object via `extractRawKey` also validates.
  //
  // No `format: 'url'` on `original_url`/sizes: the builder validates via
  // TypeBox `TypeCompiler` (DynamicForm), whose format lookup depends on a
  // global `FormatRegistry` registration that isn't reliably in scope in the
  // frontend bundle (ESM/CJS split) — an unknown format throws "Unknown format
  // 'url'" per signed field (e.g. a 12-image carousel → 60 live errors). The
  // format adds no real validation here: `url` holds a raw S3 key and the
  // signed URLs are server-generated by `transformToSchema`.
  const properties: Record<string, TSchema> = {
    url: Type.Optional(Type.String()),
    original_url: Type.Optional(Type.String()),
  };
  for (const [key, sizeSpec] of Object.entries(sizes)) {
    properties[key] = Type.Optional(
      Type.String({
        meta: { [THUMBOR_META_KEY]: sizeSpec },
      }),
    );
  }
  return Type.Object(properties, {
    title: options.title,
    description: options.description,
    meta: {
      input_type: 'image',
      picture: true,
      ...(options.additionalMeta ?? {}),
    },
  });
}
